import { SecureRoute } from "@okta/okta-react";
import { Redirect, Switch } from "react-router-dom";
import { Suspense, lazy, useEffect, useState } from "react";
import {useWebViewStore,useStore} from "../webview/useWebViewStore";
import useProfileStore from "../core/UserProfileStore";
import {ContainerAppLoader} from '../common/AppLoader';
import {AppView,WorkflowView,CustomView} from '../core/View';
import { useFeatureFlag } from '@harnessio/ff-react-client-sdk';
import { useUnifyVersion,useUnifyER } from '../../AppState';
import { useHistory , useLocation } from 'react-router-dom';
import { useApplicationStore } from "../home/ApplicationStore";
import {REACT_APP_DWM_ID} from "../../constants";
import ApplicationContainer from '../home/ApplicationContainer';
import DashboardContainer from '../home/DashboardContainer';
import MyUnifyContainer from '../home/MyUnifyContainer';
import WorkflowContainer from '../home/WorkflowContainer';
import { toRelativeUrl } from "@okta/okta-auth-js";
import useUserSettingStore from "../dashboard/UserSettingStore";
import { hasAnyDashboardAccess } from "../utils/dashboardAccessUtils";

const GlobalSearchResult = lazy(() => import('../global-search/GlobalSearchResult'));
const GlobalSearchHistory = lazy(() => import('../global-search/GlobalSearchHistory'));
const UnifyAdmin = lazy(() => import('../unify-admin/UnifyAdminContainer'));
const DealerAdmin = lazy(() => import('../dealer-admin/DealerAdminContainer'));

const root = "/unify";

const PATH ={
  DEFAULT :"default",
  MY_UNIFY : "myUnify",
  WORKFLOW :"workflows",
  APPLICATION : "applications",
  DASHBOARD: "dashboards"
}


export function UnifyRoute() {

  const {isLoading, name, setIsLoading} = useStore() as any;
  const {appView, roles, unifyUserProfile } = useProfileStore();
  const { webviews,resetActive, setCustomWebView, customView } = useWebViewStore();
  const active = webviews.find((iframe:any) => iframe.active);
  const history = useHistory();
  const location = useLocation();
  const {applications} = useApplicationStore();
  const {dashboardAccessSetting, exemptedUsers} = useUserSettingStore();
  const hasDashboardPermission = hasAnyDashboardAccess(roles,unifyUserProfile?.loginId, exemptedUsers, dashboardAccessSetting);
  
  const UNIFY_UI_20 = useFeatureFlag("UNIFY_UI_20");
  const UNIFY_UI_ER = useFeatureFlag("UNIFY_UI_ER");

  const setUnifyVersion = useUnifyVersion((state: any) => state.setUnifyVersion);
  const setUnifyER = useUnifyER((state: any) => state.setUnifyER);

  const [currentContext, setCurrentContext] = useState('');

  useEffect(()=>{
      setCurrentContext(window.location.pathname as any)
      // eslint-disable-next-line react-hooks/exhaustive-deps
  },[window.location.pathname]);

  const getDefaultRoute = () => {

    if(location.pathname.indexOf("view") !==-1){
      return;
    }

    let view = appView;
    const hasDriveWorkflow = applications.some((app: any) => app?.workflows?.some((workflow: any) => workflow.appId === REACT_APP_DWM_ID));
    const hasFavoriteApp = applications.some((app: any) => app.isFavorite);
    const hasFavoriteWorkflow = applications.some((app: any) => app?.workflows?.some((workflow: any) => workflow.isFavorite));
    if(appView === PATH.DEFAULT){
      if(hasDashboardPermission ){
        view = PATH.DASHBOARD;
      }
      else if(hasFavoriteApp || hasFavoriteWorkflow){
        view = PATH.MY_UNIFY;
      }else if(hasDriveWorkflow){
        view = PATH.WORKFLOW;
      }else{
        view = PATH.APPLICATION;
      }
    }else if(appView === 'unify'){
      view = PATH.MY_UNIFY;
    }
    return view;
    
  };

  useEffect(()=>{
      if(UNIFY_UI_20 === false){
        //console.log("UNIFY_UI_20 from Unify 2.0 to Unify",UNIFY_UI_20)
        setUnifyVersion( 'v1')
      }
      if(UNIFY_UI_ER === true && !active){
        setUnifyER('true')
        if(!window.location.pathname.includes('/login/callback')){
          const url = toRelativeUrl("/er-unify", window.location.origin);
          window.location.href=url;
        }
      }
      else if(UNIFY_UI_ER === false){
        setUnifyER('false')
      }
  },[UNIFY_UI_20, UNIFY_UI_ER, setUnifyVersion, setUnifyER, active]);

  useEffect(()=>{
    if(active){
      document.title = `CDK Unify - ${active?.name}`;
    }else{
      document.title = 'CDK Unify'
    }
  },[appView,active]);


  useEffect(()=>{
    if(location.pathname.indexOf("view") !== -1){
      return;
    }

    resetActive();
    const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming;
    if(nav?.type === 'navigate'){
      const route = getDefaultRoute();
      history.replace(`/unify/${route}`);
    }

    if (location.pathname.indexOf("/NGLSC") !== -1) {
      const params = new URLSearchParams(location.search)
      const appid = params?.get("appid")
      const url = params?.get("url")
      history.push(`/unify/view/${appid}/cta`)
      setCustomWebView({ id: appid, url: url });
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  useEffect(() => {
    const isIframeRoute =
      location.pathname.includes('/unify/aview/') ||
      location.pathname.includes('/unify/wview/');

    if (!isIframeRoute) {
      setIsLoading(false);
    }
  }, [location.pathname, setIsLoading]);

  const nonAdminStyle ={
      flex: 1, 
      margin:'0 auto', 
      maxWidth: "1664px",
      width:'100%',
  }

  const adminStyle ={
      width:'100%',
  }

  return (
      <main style={{
      ...(currentContext?.indexOf("-admin") !==-1 ? adminStyle : nonAdminStyle),
      ...(customView ? { display: 'none' } : {}),
    }} >
      <div style={currentContext?.indexOf("-admin") !==-1 ? {} : {padding:"0px 24px"}} >
        {isLoading && <ContainerAppLoader loaderText={name +" is Loading ..."} />}
       <Suspense fallback={<div>Loading page...</div>}>
        <Switch>
          <SecureRoute
            exact
            path={root}
            render={() => <Redirect to={`${root}/applications`} />}
          />
          <SecureRoute path={`${root}/home`} render={() => <Redirect to={`${root}/${getDefaultRoute()}`} />} />
          <SecureRoute path={`${root}/myUnify`} component={MyUnifyContainer} />
          <SecureRoute path={`${root}/workflows`} component={WorkflowContainer} />
          <SecureRoute path={`${root}/applications`} component={ApplicationContainer} />
            <SecureRoute
              path={`${root}/dashboards`}
              render={() => {
                if (!hasDashboardPermission) {
                  return <Redirect to={`${root}/${getDefaultRoute()}`} />
                }
                return <DashboardContainer />;
              }}
            />
          <SecureRoute path={`${root}/dealer-admin`} component={DealerAdmin} />
          <SecureRoute path={`${root}/unify-admin`} component={UnifyAdmin} />
          <SecureRoute path={`${root}/view/:id/cta`} component={CustomView} />
          <SecureRoute path={`${root}/aview/:appId/:fromUniversity/:dpLink`} component={AppView} />
          <SecureRoute path={`${root}/aview/:appId/:fromUniversity`} component={AppView} />
          <SecureRoute path={`${root}/wview/:appId/:workflowId/:fromUniversity/:dpLink`} component={WorkflowView} />
          <SecureRoute path={`${root}/wview/:appId/:workflowId/:fromUniversity`} component={WorkflowView} />          
          <SecureRoute path={`${root}/global-search`} component={GlobalSearchResult} />
          <SecureRoute path={`${root}/global-search-history`} component={GlobalSearchHistory} />
        </Switch>
      </Suspense>
      </div>
    </main>
  );
}

export default UnifyRoute;
